// ======================================================================
// File: src/modules/ppe/pages/procurement/purchase-return/form/PurchaseReturnLineDrawer.jsx
// Module: PPE → Procurement → Purchase Return
//
// Purpose:
// - Add Purchase Return line
// - Select an existing GRN line
// - Select return source: ACCEPTED / REJECTED
// - Enter return quantity
// - Follows the existing GRN line drawer UI pattern
// ======================================================================

import React, { useEffect, useState } from "react";

import purchaseReturnService from "../services/purchaseReturnService";
import grnService from "../../grn/services/grnService";
import incomingQualityService from "../../../quality/incoming/services/incomingQualityService";

import "../purchase-return-ui.css";

import SearchableDropdown from "../../../../components/common/SearchableDropdown";

export default function PurchaseReturnLineDrawer({
  open,
  purchaseReturnId,
  grnId,
  record,
  onClose,
  onSaved,
}) {

  const [form, setForm] = useState({
    grn_line_id: "",
    grn_line_label: "",

    material_type: "",
    material_id: "",
    material_label: "",

    received_qty: 0,
    accepted_qty: 0,
    rejected_qty: 0,

    uom: "",
    unit_rate: "",

    batch_no: "",
    expiry_date: "",

    return_source: "",
    return_qty: "",
  });

  //const [grnLines, setGrnLines] = useState([]);
  //const [loadingLines, setLoadingLines] = useState(false);
  //const [saving, setSaving] = useState(false);
  const [grnLines, setGrnLines] = useState([]);
const [qaLines, setQaLines] = useState([]);
const [loadingLines, setLoadingLines] = useState(false);
const [saving, setSaving] = useState(false);

const [returnableQty, setReturnableQty] = useState(0);
const [loadingReturnableQty, setLoadingReturnableQty] = useState(false);


  // ----------------------------------------------------------
  // LOAD GRN LINES
  // ----------------------------------------------------------
const loadGRNLines = async () => {

  if (!grnId) {
    setGrnLines([]);
    setQaLines([]);
    return;
  }

  try {

    setLoadingLines(true);

    const result = await grnService.getGRNById(grnId);

    const grn = result?.data || result;

    setGrnLines(
      Array.isArray(grn?.lines)
        ? grn.lines
        : []
    );

    // ----------------------------------------------------------
    // LOAD INCOMING QA FOR THIS GRN
    // ----------------------------------------------------------

    setQaLines([]);

    if (grn?.grn_no) {

      const qaListResult =
        await incomingQualityService.getInspectionList({
          grn_no: grn.grn_no
        });

      const inspections =
        Array.isArray(qaListResult?.data)
          ? qaListResult.data
          : [];

      if (inspections.length > 0) {

        const inspectionResult =
          await incomingQualityService.getInspectionById(
            inspections[0].id
          );

        const inspection =
          inspectionResult?.data || null;

        setQaLines(
          Array.isArray(inspection?.lines)
            ? inspection.lines
            : []
        );
      }
    }

  } catch (error) {

    console.error(
      "Error loading GRN / Incoming QA:",
      error
    );

    setGrnLines([]);
    setQaLines([]);

    alert(
      error?.response?.data?.message ||
      "Failed to load GRN / Incoming QA"
    );

  } finally {

    setLoadingLines(false);
  }
};

  // ----------------------------------------------------------
  // RESET FORM
  // ----------------------------------------------------------
  useEffect(() => {

    if (!open) {
      return;
    }

    setForm({
      grn_line_id: "",
      grn_line_label: "",

      material_type: "",
      material_id: "",
      material_label: "",

      received_qty: 0,
      accepted_qty: 0,
      rejected_qty: 0,

      uom: "",
      unit_rate: "",

      batch_no: "",
      expiry_date: "",

      return_source: "",
      return_qty: "",
    });

    loadGRNLines();

    // eslint-disable-next-line
  }, [open, grnId]);

  // ----------------------------------------------------------
  // SELECT GRN LINE
  // ----------------------------------------------------------
const handleGRNLineChange = (line) => {

  if (!line) {
    return;
  }

  // ----------------------------------------------------------
  // FIND MATCHING INCOMING QA LINE
  // ----------------------------------------------------------

  const qaLine = qaLines.find(
    (qa) =>
      Number(qa.grn_line_id) === Number(line.id)
  );

  setForm({
    ...form,

    grn_line_id: line.id,

    grn_line_label:
      `${line.material_name || line.material_id} - Batch: ${line.batch_no || "-"}`,

    material_type:
      line.material_type || "",

    material_id:
      line.material_id || "",

    material_label:
      line.material_name ||
      line.material_id ||
      "",

    // --------------------------------------------------------
    // RECEIVED QTY
    // --------------------------------------------------------

    received_qty:
      qaLine?.received_qty ??
      line.received_qty ??
      0,

    // --------------------------------------------------------
    // QA DECISION QTY
    // --------------------------------------------------------

    accepted_qty:
      qaLine?.accepted_qty ??
      0,

    rejected_qty:
      qaLine?.rejected_qty ??
      0,

    uom:
      line.uom || "",

    unit_rate:
      line.unit_price || "",

    batch_no:
      line.batch_no || "",

    expiry_date:
      line.expiry_date || "",

    return_source: "",
    return_qty: "",
  });
};
  // ----------------------------------------------------------
  // RETURN SOURCE CHANGE
  // ----------------------------------------------------------
  const handleReturnSourceChange = async (e) => {

  const returnSource = e.target.value;

  setForm({
    ...form,
    return_source: returnSource,
    return_qty: "",
  });

  setReturnableQty(0);

  if (!returnSource || !form.grn_line_id) {
    return;
  }

  try {

    setLoadingReturnableQty(true);

    const result =
      await purchaseReturnService.getReturnableQuantity(
        form.grn_line_id,
        returnSource
      );

    const data = result?.data || result;

    setReturnableQty(
      Number(data?.remaining_returnable_qty || 0)
    );

  } catch (error) {

    console.error(
      "Error loading returnable quantity:",
      error
    );

    setReturnableQty(0);

    alert(
      error?.response?.data?.message ||
      "Failed to load returnable quantity"
    );

  } finally {

    setLoadingReturnableQty(false);
  }
};
  // ----------------------------------------------------------
  // AVAILABLE RETURN QTY
  // ----------------------------------------------------------
  const getAvailableQty = () => {
  return Number(returnableQty || 0);
};

  // ----------------------------------------------------------
  // SAVE LINE
  // ----------------------------------------------------------
  const handleSave = async () => {

    try {

      if (!form.grn_line_id) {
        alert("Please select GRN Line");
        return;
      }

      if (!form.return_source) {
        alert("Please select Return Source");
        return;
      }

      const qty = Number(form.return_qty || 0);

      if (qty <= 0) {
        alert("Return Quantity must be greater than zero");
        return;
      }

      const availableQty = getAvailableQty();

      if (qty > availableQty) {
        alert(
          `Return Quantity cannot exceed available ${form.return_source.toLowerCase()} quantity (${availableQty})`
        );
        return;
      }

      setSaving(true);

      const payload = {
        grn_line_id: form.grn_line_id,
        return_source: form.return_source,
        return_qty: qty,
      };

      await purchaseReturnService.addPurchaseReturnLine(
        purchaseReturnId,
        payload
      );

      alert(
        "Purchase Return line added successfully"
      );

      if (onSaved) {
        onSaved();
      }

      onClose();

    } catch (error) {

      alert(
        error?.response?.data?.message ||
        "Failed to add Purchase Return line"
      );

    } finally {

      setSaving(false);
    }
  };

  // ----------------------------------------------------------
  // GRN LINE SEARCH
  // ----------------------------------------------------------
  const searchGRNLines = async (search) => {

    const text =
      (search || "").trim().toLowerCase();

    const filtered = grnLines.filter((line) => {

      const material =
        String(
          line.material_name ||
          line.material_id ||
          ""
        ).toLowerCase();

      const batch =
        String(
          line.batch_no || ""
        ).toLowerCase();

      return (
        !text ||
        material.includes(text) ||
        batch.includes(text)
      );
    });

    return filtered;
  };

  // ----------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------
  if (!open) {
    return null;
  }

  return (
    <div className="erp-drawer-overlay">

      <div className="erp-drawer">

        {/* HEADER */}
        <div className="erp-drawer-header">

          <h5>
            Add Purchase Return Line
          </h5>

          <button
            className="btn-close"
            onClick={onClose}
          />

        </div>

        {/* BODY */}
        <div className="erp-drawer-body">

          <div className="row g-3">

            {/* GRN LINE */}
            <div className="col-12">

              <SearchableDropdown
                label="GRN Line"
                value={form.grn_line_label}
                placeholder={
                  loadingLines
                    ? "Loading GRN Lines..."
                    : "Search GRN Line..."
                }
                disabled={
                  !grnId ||
                  loadingLines
                }
                fetchOptions={searchGRNLines}
                getOptionLabel={(line) =>
                  `${line.material_name || line.material_id} (Batch: ${line.batch_no || "-"})`
                }
                getOptionValue={(line) =>
                  line.id
                }
                onChange={handleGRNLineChange}
              />

            </div>

            {/* MATERIAL TYPE */}
            <div className="col-6">

              <label className="form-label">
                Material Type
              </label>

              <input
                type="text"
                className="form-control"
                value={form.material_type}
                readOnly
              />

            </div>

            {/* MATERIAL */}
            <div className="col-6">

              <label className="form-label">
                Material
              </label>

              <input
                type="text"
                className="form-control"
                value={form.material_label}
                readOnly
              />

            </div>

            {/* UOM */}
            <div className="col-6">

              <label className="form-label">
                UOM
              </label>

              <input
                type="text"
                className="form-control"
                value={form.uom}
                readOnly
              />

            </div>

            {/* RECEIVED QTY */}
            <div className="col-6">

              <label className="form-label">
                Received Qty
              </label>

              <input
                type="number"
                className="form-control"
                value={form.received_qty}
                readOnly
              />

            </div>

            {/* ACCEPTED QTY */}
            <div className="col-6">

              <label className="form-label">
                Accepted Qty
              </label>

              <input
                type="number"
                className="form-control"
                value={form.accepted_qty}
                readOnly
              />

            </div>

            {/* REJECTED QTY */}
            <div className="col-6">

              <label className="form-label">
                Rejected Qty
              </label>

              <input
                type="number"
                className="form-control"
                value={form.rejected_qty}
                readOnly
              />

            </div>

            {/* BATCH */}
            <div className="col-6">

              <label className="form-label">
                Batch No
              </label>

              <input
                type="text"
                className="form-control"
                value={form.batch_no}
                readOnly
              />

            </div>

            {/* EXPIRY */}
            <div className="col-6">

              <label className="form-label">
                Expiry Date
              </label>

              <input
                type="date"
                className="form-control"
                value={form.expiry_date || ""}
                readOnly
              />

            </div>

            {/* RETURN SOURCE */}
            <div className="col-6">

              <label className="form-label">
                Return Source
                <span className="text-danger">
                  *
                </span>
              </label>

              <select
                className="form-select"
                value={form.return_source}
                onChange={handleReturnSourceChange}
                disabled={!form.grn_line_id}
              >

                <option value="">
                  Select Return Source
                </option>

                <option
                  value="ACCEPTED"
                  disabled={
                    Number(form.accepted_qty || 0) <= 0
                  }
                >
                  Accepted
                </option>

                <option
                  value="REJECTED"
                  disabled={
                    Number(form.rejected_qty || 0) <= 0
                  }
                >
                  Rejected
                </option>

              </select>

            </div>

            {/* AVAILABLE QTY */}
            <div className="col-6">

              <label className="form-label">
                Available Return Qty
              </label>

              <input
                type="number"
                className="form-control"
                value={
                    loadingReturnableQty
                      ? ""
                      : getAvailableQty()
                  }
                readOnly
              />

            </div>

            {/* RETURN QTY */}
            <div className="col-6">

              <label className="form-label">
                Return Qty
                <span className="text-danger">
                  *
                </span>
              </label>

              <input
                type="number"
                min="0"
                step="any"
                className="form-control"
                value={form.return_qty}
                onChange={(e) =>
                  setForm({
                    ...form,
                    return_qty: e.target.value,
                  })
                }
                disabled={!form.return_source}
              />

            </div>

            {/* UNIT RATE */}
            <div className="col-6">

              <label className="form-label">
                Unit Rate
              </label>

              <input
                type="number"
                className="form-control"
                value={form.unit_rate}
                readOnly
              />

            </div>

          </div>

        </div>

        {/* FOOTER */}
        <div className="erp-drawer-footer">

          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={
              saving ||
              loadingLines ||
              loadingReturnableQty
            }
          >
            {saving
              ? "Saving..."
              : "Save Line"}
          </button>

        </div>

      </div>

    </div>
  );
}